import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, tap} from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AppLimits } from '../models/app';
import { DeviceType } from '../models/device';
import { Kind } from '../models/kind';
import { DeviceService } from './device.service';
import {ScgHttpWrapper} from '@app/core/injectables/scg-http-wrapper';
import { AuthService } from '../auth/auth.service';
import {Language} from '@app/core/constants/language';
import {TranslateService} from '@ngx-translate/core';

/**
 * Array with the names of the custom groups. Ordered by custom group number.
 *
 * Hint: You can get the name of a specific custom group by subtracting the enum value of `CustomGroup1`.
 *
 * Example for accessing the name of custom group 3:
 * ```typescript
 * const customGroup3Name = customGroupNames[Kind.CustomGroup3 - Kind.CustomGroup1];
 * ```
 */
export type CustomGroupNames = [string, string, string, string, string];

@Injectable({
  providedIn: 'root'
})
export class GroupService {
  /** Caches customGroupNames. Names will be lazy-loaded (not loaded automatically on app init).  */
  private customGroupNamesSubject = new BehaviorSubject<CustomGroupNames>(null);
  private customGroupNamesLoadingTriggered = false;

  /**
   * Returns custom group names which must eventually be requested via API if not already happened.
   * Also emits when the custom group names were changed.
   *
   * **Remember to check if you need to unsubscribe when using this!**
   */
  public get customGroupNames(): Observable<CustomGroupNames> {
    // Check if custom group names were already requested before
    if (!this.customGroupNamesLoadingTriggered) {

      this.customGroupNamesLoadingTriggered = true;
      // onDeviceSwitch ist ein BehaviorSubject (gefiltert auf != null): der Subscriber
      // feuert sofort für das bereits aktive Gerät UND bei jedem späteren Wechsel.
      // Daher KEIN zusätzlicher expliziter getCustomGroupNames()-Aufruf hier:
      // Beim direkten Aufruf einer Deeplink-Route (z.B. /app-details) ist noch kein
      // Gerät aktiv -> ein sofortiger Aufruf würde ohne Geräte-Kontext leere
      // Gruppennamen laden und in den Subject schreiben (Dropdown zeigt dann "#1" ohne Name).
      this.device.onDeviceSwitch.subscribe(() => {
        this.getCustomGroupNames();
      });
    }

    return this.customGroupNamesSubject
      .asObservable()
      .pipe(filter(value => value != null));
  }

  constructor(
    private scgHttp: ScgHttpWrapper,
    private auth: AuthService,
    private device: DeviceService,
    private translate: TranslateService,
    private log: NGXLogger
  ) { }

  /**
   * Needs to be called at the start of the app.
   */
  public init() {
    this.customGroupNamesSubject = new BehaviorSubject<CustomGroupNames>(null);

    this.auth.onLogout.subscribe(() => {
      this.customGroupNamesSubject.complete();

      this.customGroupNamesLoadingTriggered = false;
    });
  }

  /**
   * Update the group name of one or more custom groups
   */
  public setCustomGroupName(newName: {group: Kind, name: string}): Observable<any> {
    let url = environment.apiUrl + 'cbsettings/changegroupname';

    if (this.device.activeDevice?.device.type === DeviceType.CC) {
      url = environment.apiUrl + this.device.activeDevice.username + '/ccsettings/changegroupname';
    }

    const body = {
      groupkind: newName.group,
      groupName: newName.name
    };

    return this.scgHttp.post<any>(url, body)
      .pipe(
        tap(() => {
          // Notify about the new name if the custom group names are already cached
          const oldNames = this.customGroupNamesSubject.value;
          if (oldNames != null) {
            const idx = newName.group - Kind.CustomGroup1;
            const nextNames = [...oldNames] as CustomGroupNames;
            nextNames[idx] = newName.name;
            this.customGroupNamesSubject.next(nextNames);
          }
        })
      );
  }

  /**
   * Get limits for custom groups and group internet (other groups are not supported)
   */
  public getGroupLimitsCb(group: Kind): Observable<AppLimits> {
    return this.scgHttp.get<ApiGroupLimitsCb>(environment.apiUrl + 'cblists/groupdefaults/' + (group - 10))
      .pipe(
        map<ApiGroupLimitsCb, AppLimits>(result => {
          const appLimits = new AppLimits();
          appLimits.isTimeLimitActive       = result.isTimeLimitActive    ?? appLimits.isTimeLimitActive;
          appLimits.timeLimitDay            = result.timeLimitDay         ?? appLimits.timeLimitDay;
          appLimits.timeLimitWeek           = result.timeLimitWeek        ?? appLimits.timeLimitWeek;
          appLimits.timeLimitMonth          = result.timeLimitMonth       ?? appLimits.timeLimitMonth;
          appLimits.isWeekdayLimitActive    = result.isWeekdayLimitActive ?? appLimits.isWeekdayLimitActive;
          appLimits.weekdayLimits.monday    = result.timeLimitMonday      ?? appLimits.weekdayLimits.monday;
          appLimits.weekdayLimits.tuesday   = result.timeLimitTuesday     ?? appLimits.weekdayLimits.tuesday;
          appLimits.weekdayLimits.wednesday = result.timeLimitWednesday   ?? appLimits.weekdayLimits.wednesday;
          appLimits.weekdayLimits.thursday  = result.timeLimitThursday    ?? appLimits.weekdayLimits.thursday;
          appLimits.weekdayLimits.friday    = result.timeLimitFriday      ?? appLimits.weekdayLimits.friday;
          appLimits.weekdayLimits.saturday  = result.timeLimitSaturday    ?? appLimits.weekdayLimits.saturday;
          appLimits.weekdayLimits.sunday    = result.timeLimitSunday      ?? appLimits.weekdayLimits.sunday;

          appLimits.blocktimes = result.blockedTimesCB ?? appLimits.blocktimes;
          appLimits.optBonusType = result.optBonusType ?? 0;
          return appLimits;
        })
      );
  }

  /**
   * Get limits for custom groups and internet group (other groups are not supported)
   */
  public getGroupLimitsCc(group: Kind): Observable<AppLimits> {
    if (this.device.activeDevice?.username == null) {
      this.log.error('Could not get username for CC API request');
      return new Observable(null);
    }

    return this.scgHttp.get<ApiGroupLimitsCc>(
      environment.apiUrl + this.device.activeDevice.username + '/cclists/groupdefaults/' + (group - 10)
    )
      .pipe(
        map<ApiGroupLimitsCc, AppLimits>(result => {
          const appLimits = new AppLimits();
          appLimits.isTimeLimitActive       = result.isTimeLimitActive    ?? appLimits.isTimeLimitActive;
          appLimits.timeLimitDay            = result.timeLimitDay         ?? appLimits.timeLimitDay;
          appLimits.timeLimitWeek           = result.timeLimitWeek        ?? appLimits.timeLimitWeek;
          appLimits.timeLimitMonth          = result.timeLimitMonth       ?? appLimits.timeLimitMonth;
          appLimits.isWeekdayLimitActive    = result.isWeekdayLimitActive ?? appLimits.isWeekdayLimitActive;
          appLimits.weekdayLimits.monday    = result.timeLimitMonday      ?? appLimits.weekdayLimits.monday;
          appLimits.weekdayLimits.tuesday   = result.timeLimitTuesday     ?? appLimits.weekdayLimits.tuesday;
          appLimits.weekdayLimits.wednesday = result.timeLimitWednesday   ?? appLimits.weekdayLimits.wednesday;
          appLimits.weekdayLimits.thursday  = result.timeLimitThursday    ?? appLimits.weekdayLimits.thursday;
          appLimits.weekdayLimits.friday    = result.timeLimitFriday      ?? appLimits.weekdayLimits.friday;
          appLimits.weekdayLimits.saturday  = result.timeLimitSaturday    ?? appLimits.weekdayLimits.saturday;
          appLimits.weekdayLimits.sunday    = result.timeLimitSunday      ?? appLimits.weekdayLimits.sunday;

          appLimits.blocktimes = result.blockTimes ?? appLimits.blocktimes;
          appLimits.tagDetails = result.tagDetails ?? '00:15|00:30';
          return appLimits;
        })
      );
  }

  private translatedGroupNames(groupNames) {
    let retval = groupNames;
    if (retval !== 'Gruppe1,Gruppe2,Gruppe3,Gruppe4,Gruppe5') return retval; // already customized
    if (this.translate.currentLang === Language.English) retval = 'Group1,Group2,Group3,Group4,Group5';
    if (this.translate.currentLang === Language.French) retval = 'Groupe1,Groupe2,Groupe3,Groupe4,Groupe5';
    return retval;
  }

  private getCustomGroupNames() {
    // Ohne aktives Gerät darf nicht geladen werden: der Request hätte keinen Geräte-Kontext
    // und würde leere/falsche Gruppennamen in den Subject schreiben. Der onDeviceSwitch-
    // Subscriber löst getCustomGroupNames() erneut aus, sobald ein Gerät aktiv ist.
    if (this.device.activeDevice == null) {
      return;
    }

    let url = environment.apiUrl + 'cbsettings/groupnames';

    if (this.device.activeDevice?.device.type === DeviceType.CC) {
      url = environment.apiUrl + 'allusers/ccsettings/groupnames';
    }

    this.scgHttp.get<{groupnames: string}>(url)
      .pipe(
        map<{groupnames: string}, CustomGroupNames>(data => {
          const groupNames = this.translatedGroupNames(data.groupnames);
          const responseArr = groupNames.split(',');
          return [responseArr[0], responseArr[1], responseArr[2], responseArr[3], responseArr[4]];
        })
      )
      .subscribe({
        next: customGroupNames => {
          this.customGroupNamesSubject.next(customGroupNames);
        },
        error: (error: HttpErrorResponse) => {
          // Currently, the subscribers of customGroupNames will never get their result if we end up here
          this.log.error('Error loading custom group names');
          this.customGroupNamesLoadingTriggered = false;
        }
      });
  }
}

interface ApiGroupLimits {
  isTimeLimitActive: boolean;
  timeLimitDay: string;
  timeLimitWeek: string;
  timeLimitMonth: string;
  isWeekdayLimitActive: boolean;
  timeLimitMonday: string;
  timeLimitTuesday: string;
  timeLimitWednesday: string;
  timeLimitThursday: string;
  timeLimitFriday: string;
  timeLimitSaturday: string;
  timeLimitSunday: string;
}

interface ApiGroupLimitsCb extends ApiGroupLimits {
  blockedTimesCB: string;
  optBonusType: number;
}

interface ApiGroupLimitsCc extends ApiGroupLimits {
  blockTimes: string;
  tagDetails: string;
}
